import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ReactiveJsonRoot } from '@ea-lab/reactive-json'
import yaml from 'js-yaml'
import {
  ChartColumn, ChartBarBig, ChartLine, ChartPie, Hash, Table2, List,
  Plus, X, Undo2, Search, Copy, Check, Save, Trash2, Code2, Eye, Sparkles, Send,
} from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import { usePanneauDroit } from '../shell/cornerDock'
import {
  METRIC_OPS, BUCKET_OPS, COLUMN_OPS, INTERVALS, TICK_UNITS, RANGES, TYPES, SLOTS,
  mk, fieldMap, isAggregatable, dimLabel, getSlot, setSlot, emptyViz, switchType,
  placeOnWorkspace, placeOnSlot, missingSlots, vizTitle, buildQuery, baseQuery,
  buildBlock, exportBlock, suggestions, statsNeeded, errorMessage, toAiState, fromAiState,
} from './lensModel'

/**
 * Lens — éditeur de visualisation sur les index Elasticsearch d'Orbit.
 *
 * Pourquoi un composant React (porte config-first) : c'est un ÉDITEUR — un
 * état d'édition manipulé au glisser-déposer, d'où l'on tire une requête
 * d'agrégation ET un rjBuild. Aucun YAML ni plugin enregistré ne compose une
 * requête à partir d'un état (même cas qu'EcaEditor ou le builder). Le RENDU,
 * lui, n'est pas écrit ici : c'est un bloc reactive-json `ChartLoader`
 * (Chart.js), monté en direct sur la réponse, et exportable tel quel.
 *
 * Contrats :
 *   - GET  /api/orbit/lens/indexes                 (module orbit_lens)
 *   - GET  /api/orbit/lens/indexes/{index}/fields  (champs indexés, filtres d'accès)
 *   - POST /api/elasticsearch/{index}?es_filters[]= (route brute d'elasticsearch_endpoint)
 *   - GET|POST /api/orbit/saved-view/lens          (orbit_saved_view, bucket `ui`)
 *   - POST /api/elasticsearch/query/save           (requête stockée, pour l'export)
 */

const VIEW_KEY = 'lens'

async function request(url, init = {}) {
  const res = await fetch(url, {
    credentials: 'include',
    ...init,
    headers: {
      Accept: 'application/json',
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init.headers || {}),
    },
  })
  const body = await res.json().catch(() => ({}))
  if (!res.ok) {
    const err = new Error(errorMessage(body, res.status))
    err.status = res.status
    throw err
  }
  return body
}

function runQuery(index, body, accessFilters, signal) {
  const qs = (accessFilters || []).map((f) => `es_filters[]=${encodeURIComponent(f)}`).join('&')
  return request(`/api/elasticsearch/${encodeURIComponent(index)}${qs ? `?${qs}` : ''}`, {
    method: 'POST',
    body: JSON.stringify(body),
    signal,
  })
}

const KIND_BADGE = {
  date: { cls: 'badge-info', txt: 'Date' },
  number: { cls: 'badge-success', txt: 'Nombre' },
  string: { cls: 'badge-warning', txt: 'Texte' },
  boolean: { cls: 'badge-accent', txt: 'Booléen' },
  text: { cls: 'badge-neutral', txt: 'Plein texte' },
}

const TYPE_ICON = {
  bar: ChartColumn, bar_h: ChartBarBig, line: ChartLine, doughnut: ChartPie, metric: Hash, table: Table2, list: List,
}

// Libellés courts des boutons de type (le libellé complet reste en `title`).
const TYPE_SHORT = {
  bar: 'Barres', bar_h: 'Barres horiz.', line: 'Lignes', doughnut: 'Anneau', metric: 'Métrique', table: 'Tableau', list: 'Liste',
}

const NF = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 })

/**
 * La réponse brute d'Elasticsearch (`hits.hits[]._source`) sous la forme que
 * lit la variante `rows` de ChartLoader (`contents[]`, celle de l'enveloppe
 * d'une requête enregistrée) : l'aperçu et le bloc exporté lisent pareil.
 */
const asContents = (response) => (response ? { ...response, contents: (response.hits?.hits ?? []).map((h) => h._source ?? {}) } : response)
const fmt = (v) => (v == null || Number.isNaN(v) ? 'n/d' : NF.format(v))

/* ------------------------------------------------------------------ */

/**
 * Monte un bloc reactive-json `ChartLoader` sur une réponse déjà chargée.
 * `key` : ReactiveJsonRoot ne rejoue pas un rjBuild changé — on le remonte.
 */
function BlockView({ block, response, responseId, plugins }) {
  const rjBuild = useMemo(() => ({
    renderView: [{ type: 'ChartLoader', data: '~~.response', ...block }],
    data: { response },
  }), [block, response])
  const key = `${responseId}:${JSON.stringify(block)}`
  return <ReactiveJsonRoot key={key} maybeRawAppRjBuild={rjBuild} plugins={plugins} />
}

function CopyButton({ text, label }) {
  const [done, setDone] = useState('')
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text)
      setDone('Copié.')
    }
    catch {
      setDone('Copie refusée par le navigateur : sélectionnez le texte.')
    }
    setTimeout(() => setDone(''), 2500)
  }
  return (
    <span className="inline-flex items-center gap-2">
      <button type="button" className="btn btn-secondary btn-sm" onClick={copy}>
        {done === 'Copié.' ? <Check size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}
        {label}
      </button>
      <span role="status" className="text-xs text-slate-500">{done}</span>
    </span>
  )
}

/* ------------------------------------------------------------------ */

function FieldStats({ field, index, ctx, accessFilters }) {
  const [state, setState] = useState({ loading: true })
  const filterKey = JSON.stringify(baseQuery(ctx))
  useEffect(() => {
    const ac = new AbortController()
    const aggs = field.kind === 'number' || field.kind === 'date'
      ? { s: { stats: { field: field.aggField } } }
      : { t: { terms: { field: field.aggField, size: 5 } } }
    runQuery(index, { size: 0, track_total_hits: true, query: JSON.parse(filterKey), aggs }, accessFilters, ac.signal)
      .then((r) => setState({ data: r }))
      .catch((e) => { if (e.name !== 'AbortError') setState({ error: e.message }) })
    return () => ac.abort()
  }, [field, index, filterKey, accessFilters])

  if (state.loading) return <p className="text-xs text-slate-500">Chargement…</p>
  if (state.error) return <p className="text-xs text-rose-700">{state.error}</p>
  const total = state.data?.hits?.total?.value ?? 0
  const agg = state.data?.aggregations || {}
  if (!total) return <p className="text-xs text-slate-500">Aucune valeur sur la période.</p>
  if (agg.s) {
    const s = agg.s
    if (!s.count) return <p className="text-xs text-slate-500">Champ vide sur la période.</p>
    return field.kind === 'date'
      ? <p className="text-xs tabular-nums">du {new Date(s.min).toLocaleDateString('fr-FR')} au {new Date(s.max).toLocaleDateString('fr-FR')} · {fmt(s.count)} valeurs</p>
      : <p className="text-xs tabular-nums">min {fmt(s.min)} · moy. {fmt(s.avg)} · max {fmt(s.max)}</p>
  }
  const buckets = agg.t?.buckets || []
  if (!buckets.length) return <p className="text-xs text-slate-500">Champ vide sur la période.</p>
  return (
    <ul className="space-y-1.5">
      {buckets.map((b) => {
        const p = (b.doc_count / total) * 100
        const label = b.key_as_string ?? String(b.key)
        return (
          <li key={label} className="text-xs">
            <div className="flex justify-between gap-2">
              <span className="truncate" title={label}>{label}</span>
              <span className="tabular-nums text-slate-500">{fmt(p)} %</span>
            </div>
            <div className="progress progress-sm" aria-hidden="true"><div className="progress-fill" style={{ width: `${p}%` }} /></div>
          </li>
        )
      })}
    </ul>
  )
}

/* ------------------------------------------------------------------ */

function DimensionEditor({ slot, dim, fields, onChange, onRemove, onClose }) {
  const ops = slot.kind === 'column' ? COLUMN_OPS : slot.kind === 'metric' ? METRIC_OPS : BUCKET_OPS
  const op = ops[dim.op]
  // Une colonne de liste lit le document tel quel : tout champ convient, même
  // plein texte ; une agrégation, elle, exige un champ agrégeable.
  const compatible = (o) => (o.kinds ? fields.filter((f) => (slot.kind === 'column' || isAggregatable(f)) && o.kinds.includes(f.kind)) : [])
  const fmap = fieldMap(fields)
  const setOp = (k) => {
    const o = ops[k]
    let field = dim.field
    if (!o.kinds) field = null
    else if (!o.kinds.includes(fmap.get(field)?.kind)) field = compatible(o)[0]?.name ?? null
    const params = k === 'date_histogram' || k === 'histogram' ? { interval: 'auto' }
      : k === 'terms' ? { size: 5, orderBy: 'count', other: false }
        : k === 'pct' ? { p: 95 } : {}
    onChange({ ...dim, op: k, field, params, auto: false })
  }
  const setParam = (p) => onChange({ ...dim, params: { ...dim.params, ...p }, auto: false })
  const headingId = `dim-${dim.id}`

  return (
    <section className="flex flex-col gap-4" aria-labelledby={headingId}>
      <div className="flex items-center justify-between gap-2">
        <h2 id={headingId} className="h-section">{slot.label}</h2>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>Fermer</button>
      </div>

      <fieldset>
        <legend className="field-label">Fonction</legend>
        <div className="grid grid-cols-2 gap-1.5">
          {Object.entries(ops).map(([k, o]) => (
            <button
              key={k}
              type="button"
              className="btn btn-toggle btn-sm justify-start"
              aria-pressed={dim.op === k}
              disabled={Boolean(o.kinds) && !compatible(o).length}
              onClick={() => setOp(k)}
            >
              {o.label}
            </button>
          ))}
        </div>
      </fieldset>

      {op?.kinds ? (
        <label className="block">
          <span className="field-label">Champ</span>
          <select className="select w-full" value={dim.field || ''} onChange={(e) => onChange({ ...dim, field: e.target.value, auto: false })}>
            {compatible(op).map((f) => <option key={f.name} value={f.name}>{f.label ? `${f.label} (${f.name})` : f.name}</option>)}
          </select>
        </label>
      ) : null}

      {dim.op === 'field' ? (
        <label className="block">
          <span className="field-label">Format</span>
          <select className="select w-full" value={dim.params?.format || ''} onChange={(e) => setParam({ format: e.target.value })}>
            <option value="">Automatique (selon le champ)</option>
            <option value="number">Nombre</option>
            <option value="text">Texte (tel quel : une année, un code)</option>
          </select>
        </label>
      ) : null}

      {dim.op === 'date_histogram' ? (
        <label className="block">
          <span className="field-label">Intervalle</span>
          <select className="select w-full" value={dim.params?.interval || 'auto'} onChange={(e) => setParam({ interval: e.target.value })}>
            {Object.entries(INTERVALS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </label>
      ) : null}

      {dim.op === 'date_histogram' && slot.key === 'x' ? (
        <label className="block">
          <span className="field-label">Graduation de l’axe</span>
          <select className="select w-full" value={dim.params?.tickUnit || 'auto'} onChange={(e) => setParam({ tickUnit: e.target.value })}>
            {Object.entries(TICK_UNITS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <span className="field-hint">Les points gardent leur intervalle ; seuls les repères de l’axe changent (courbes et barres).</span>
        </label>
      ) : null}

      {dim.op === 'histogram' ? (
        <label className="block">
          <span className="field-label">Largeur d'intervalle</span>
          <input
            className="input w-full"
            type="number"
            min="0"
            placeholder="Automatique"
            value={dim.params?.interval === 'auto' ? '' : dim.params?.interval ?? ''}
            onChange={(e) => setParam({ interval: e.target.value === '' ? 'auto' : Math.max(0, Number(e.target.value)) || 'auto' })}
          />
        </label>
      ) : null}

      {dim.op === 'terms' ? (
        <>
          <label className="block">
            <span className="field-label">Nombre de valeurs</span>
            <input
              className="input w-full"
              type="number"
              min="1"
              max="100"
              value={dim.params?.size || 5}
              onChange={(e) => setParam({ size: Math.max(1, Math.min(100, Number(e.target.value) || 1)) })}
            />
          </label>
          <label className="block">
            <span className="field-label">Trier par</span>
            <select className="select w-full" value={dim.params?.orderBy || 'count'} onChange={(e) => setParam({ orderBy: e.target.value })}>
              <option value="count">Nombre d'enregistrements</option>
              <option value="metric">Première métrique</option>
              <option value="key">Ordre alphabétique</option>
              <option value="field" disabled={!fields.some((f) => f.kind === 'number' && isAggregatable(f))}>Un autre champ (rang)</option>
            </select>
          </label>
          {dim.params?.orderBy === 'field' ? (
            <div className="flex gap-2">
              <label className="block flex-1">
                <span className="field-label">Champ d'ordre</span>
                <select
                  className="select w-full"
                  value={dim.params?.orderField || ''}
                  onChange={(e) => setParam({ orderField: e.target.value })}
                >
                  <option value="">Choisir…</option>
                  {fields.filter((f) => f.kind === 'number' && isAggregatable(f)).map((f) => (
                    <option key={f.name} value={f.name}>{f.label ? `${f.label} (${f.name})` : f.name}</option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="field-label">Sens</span>
                <select className="select" value={dim.params?.orderDir || 'asc'} onChange={(e) => setParam({ orderDir: e.target.value })}>
                  <option value="asc">Croissant</option>
                  <option value="desc">Décroissant</option>
                </select>
              </label>
            </div>
          ) : null}
          {slot.key === 'breakdown' ? (
            <label className="checkbox-row">
              <input type="checkbox" className="checkbox" checked={Boolean(dim.params?.other)} onChange={(e) => setParam({ other: e.target.checked })} />
              <span>Regrouper le reste dans « Autres » (métrique « Nombre »)</span>
            </label>
          ) : null}
        </>
      ) : null}

      {slot.kind === 'metric' ? (
        <label className="block">
          <span className="field-label">Décimales</span>
          <input
            className="input w-full"
            type="number"
            min="0"
            max="6"
            placeholder="Automatique"
            value={dim.params?.decimals ?? ''}
            onChange={(e) => setParam({ decimals: e.target.value === '' ? '' : Math.max(0, Math.min(6, Number(e.target.value) || 0)) })}
          />
        </label>
      ) : null}

      {dim.op === 'pct' ? (
        <label className="block">
          <span className="field-label">Percentile</span>
          <input
            className="input w-full"
            type="number"
            min="1"
            max="99"
            value={dim.params?.p || 95}
            onChange={(e) => setParam({ p: Math.max(1, Math.min(99, Number(e.target.value) || 95)) })}
          />
        </label>
      ) : null}

      <label className="block">
        <span className="field-label">Nom affiché</span>
        <input
          className="input w-full"
          type="text"
          value={dim.label || ''}
          placeholder={dimLabel({ ...dim, label: '' }, fields)}
          onChange={(e) => onChange({ ...dim, label: e.target.value })}
        />
      </label>

      <div className="flex justify-between gap-2 pt-3 border-t border-slate-200">
        <button type="button" className="btn btn-ghost-danger btn-sm" onClick={onRemove}>Supprimer la dimension</button>
        <button type="button" className="btn btn-primary btn-sm" onClick={onClose}>Terminé</button>
      </div>
    </section>
  )
}

/* ------------------------------------------------------------------ */

const TURN_CLASS = {
  user: 'self-end max-w-prose rounded-xl bg-iris-50 border border-iris-100 px-3 py-2 text-sm',
  assistant: 'self-start max-w-prose rounded-xl bg-white border border-slate-200 px-3 py-2 text-sm',
  note: 'self-start text-xs text-slate-500',
  error: 'self-start alert alert-danger text-sm',
}

/** Largeur de la colonne de l'assistant (bornée par la fenêtre sur petit écran). */
const AI_PANEL_WIDTH = 420

const panelWidth = () => (typeof window === 'undefined'
  ? AI_PANEL_WIDTH
  : Math.min(AI_PANEL_WIDTH, window.innerWidth))

/**
 * Conversation avec l'assistant, en COLONNE à droite : l'application se
 * rétrécit d'autant (`usePanneauDroit` … `pousse`, le mécanisme de l'assistant
 * du builder) — on voit la visualisation pendant qu'on demande de la changer.
 * Chaque réponse est APPLIQUÉE (et s'annule par « Annuler »), le texte dit ce
 * qui a changé.
 */
function AssistantPanel({ chat, busy, onAsk, onClose }) {
  const [draft, setDraft] = useState('')
  const [width, setWidth] = useState(panelWidth)
  const listRef = useRef(null)
  usePanneauDroit('lens-ai', true, width, { pousse: true })
  useEffect(() => {
    const onResize = () => setWidth(panelWidth())
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])
  useEffect(() => {
    const el = listRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [chat, busy])
  const send = (e) => {
    e.preventDefault()
    const text = draft.trim()
    if (!text || busy) return
    setDraft('')
    onAsk(text)
  }
  return (
    <aside
      className="fixed inset-y-0 right-0 z-[200] flex flex-col bg-white border-l border-slate-200 shadow-xl"
      style={{ width }}
      aria-labelledby="lens-ai-title"
    >
      <div className="card-header flex items-center justify-between gap-2">
        <h2 id="lens-ai-title" className="flex items-center gap-2"><Sparkles size={16} aria-hidden="true" className="text-iris-600" />Assistant IA</h2>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onClose} aria-label="Fermer l'assistant IA">
          <X size={16} aria-hidden="true" />
        </button>
      </div>
      <ol ref={listRef} className="flex-1 min-h-0 overflow-y-auto p-4 flex flex-col gap-2 bg-slate-50" aria-live="polite">
        {chat.length ? null : (
          <li className="text-sm text-slate-500">
            Décrivez le graphique ou le tableau voulu, ou la modification à apporter : « nombre de transactions par mois ventilé par BU »,
            « passe en lignes », « seulement les 7 derniers jours », « top 10 des auteurs en tableau »…
          </li>
        )}
        {chat.map((t, i) => (
          <li key={i} className={TURN_CLASS[t.role]}>
            {t.role === 'user' ? <span className="sr-only">Vous : </span> : null}
            {t.role === 'assistant' ? <span className="sr-only">Assistant : </span> : null}
            {t.text}
          </li>
        ))}
        {busy ? <li className={TURN_CLASS.note} role="status"><span className="spinner" /> Claude construit la visualisation…</li> : null}
      </ol>
      <form className="p-3 border-t border-slate-200 flex flex-col gap-2" onSubmit={send}>
        <label className="block">
          <span className="sr-only">Demande à l'assistant</span>
          <textarea
            className="textarea w-full"
            rows={3}
            value={draft}
            placeholder="Votre demande… (Entrée pour envoyer, Maj+Entrée pour aller à la ligne)"
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) send(e) }}
          />
        </label>
        <button type="submit" className="btn btn-primary self-end" disabled={busy || !draft.trim()}>
          <Send size={16} aria-hidden="true" />Envoyer
        </button>
      </form>
    </aside>
  )
}

/* ------------------------------------------------------------------ */

export function LensEditor({ plugins }) {
  const { isAdmin, hasPermission } = useAuth()
  const canStoreQuery = isAdmin || hasPermission('administer elasticsearch queries')

  // Catalogue
  const [indexes, setIndexes] = useState(null)
  const [catalogError, setCatalogError] = useState('')
  const [index, setIndex] = useState('')
  const [schema, setSchema] = useState(null) // { fields, timeField, accessFilters, label }

  // Contexte de requête
  const [timeField, setTimeField] = useState(null)
  const [range, setRange] = useState('30d')
  const [qDraft, setQDraft] = useState('')
  const [query, setQuery] = useState('')

  // Visualisation + historique
  const [viz, setViz] = useState(() => emptyViz(null))
  const [hist, setHist] = useState([])
  const commit = useCallback((n) => {
    setHist((h) => [...h.slice(-30), viz])
    setViz(n)
  }, [viz])

  // Réponse
  const [response, setResponse] = useState(null)
  const [responseId, setResponseId] = useState(0)
  const [queryError, setQueryError] = useState('')
  const [loading, setLoading] = useState(false)
  const [stats, setStats] = useState({})

  // UI
  const [drag, setDrag] = useState(null)
  const [over, setOver] = useState(null)
  const [edit, setEdit] = useState(null)
  const [pop, setPop] = useState(null)
  const [fieldSearch, setFieldSearch] = useState('')
  const [panel, setPanel] = useState(null) // 'inspect' | 'export' | null

  // Vues enregistrées
  const [views, setViews] = useState([])
  const [viewId, setViewId] = useState('')
  const [saveName, setSaveName] = useState(null)
  const [viewMsg, setViewMsg] = useState('')
  const [queryUuid, setQueryUuid] = useState('')
  // Visualisation PUBLIÉE (nœud lens_visualization) que cet état met à jour.
  const [published, setPublished] = useState(null) // {id, title} | null
  const [publishName, setPublishName] = useState(null)
  const [publishMsg, setPublishMsg] = useState('')
  const [storeMsg, setStoreMsg] = useState('')

  // Assistant IA
  const [aiOpen, setAiOpen] = useState(true)
  const [chat, setChat] = useState([]) // [{role: user|assistant|note|error, text}]
  const [aiBusy, setAiBusy] = useState(false)
  const restored = useRef(null)

  const fields = useMemo(() => schema?.fields ?? [], [schema])
  const dateFields = useMemo(() => fields.filter((f) => f.kind === 'date'), [fields])
  const ctx = useMemo(() => ({ fields, timeField, range, query, stats }), [fields, timeField, range, query, stats])

  /* ---- Chargement : catalogue + dernier état ---- */
  useEffect(() => {
    // `/lens?visualization=<id>` : rouvrir une visualisation PUBLIÉE (lien
    // « Modifier dans Lens » du bloc « Visualisation »). Elle l'emporte sur le
    // dernier état mémorisé.
    const publishedId = new URLSearchParams(window.location.search).get('visualization')
    Promise.all([
      request('/api/orbit/lens/indexes'),
      request(`/api/orbit/saved-view/${VIEW_KEY}`).catch(() => null),
      publishedId ? request(`/api/orbit/lens/visualizations/${encodeURIComponent(publishedId)}/state`).catch((e) => ({ error: e.message })) : null,
    ]).then(([cat, saved, pub]) => {
      setIndexes(cat.indexes || [])
      setViews(saved?.views || [])
      if (pub?.error) setPublishMsg(`Visualisation ${publishedId} : ${pub.error}`)
      const ui = pub?.state?.index
        ? { ...pub.state, viewId: '', published: { id: pub.id, title: pub.title } }
        : saved?.auto?.state?.ui
      const known = (id) => (cat.indexes || []).some((i) => i.id === id)
      if (ui && known(ui.index)) {
        restored.current = ui
        setIndex(ui.index)
      }
      else if (cat.indexes?.length) {
        setIndex((cat.indexes.find((i) => i.id === 'node') || cat.indexes[0]).id)
      }
    }).catch((e) => setCatalogError(e.message))
  }, [])

  /* ---- Champs de l'index ---- */
  useEffect(() => {
    if (!index) return undefined
    let alive = true
    setSchema(null)
    setResponse(null)
    setStats({})
    request(`/api/orbit/lens/indexes/${encodeURIComponent(index)}/fields`)
      .then((s) => {
        if (!alive) return
        setSchema(s)
        let ui = restored.current
        restored.current = null
        // Proposition de l'assistant qui a changé d'index : elle ne se valide
        // qu'avec les champs de CET index, qu'on vient seulement de recevoir.
        if (ui?.aiState && ui.index === index) {
          const r = fromAiState(ui.aiState, s.fields)
          ui = { ...ui, ...r }
          if (r.warnings.length) setChat((c) => [...c, { role: 'note', text: r.warnings.join(' ') }])
        }
        if (ui && ui.index === index) {
          setTimeField(ui.timeField !== undefined ? ui.timeField : s.timeField)
          setRange(ui.range || '30d')
          setQDraft(ui.query || '')
          setQuery(ui.query || '')
          setViz(ui.viz || emptyViz(s.timeField))
          setQueryUuid(ui.queryUuid || '')
          setViewId(ui.viewId || '')
          setPublished(ui.published || null)
        }
        else {
          setTimeField(s.timeField)
          setViz(emptyViz(s.timeField))
          setQueryUuid('')
          setViewId('')
          setPublished(null)
        }
        setHist([])
        setEdit(null)
        setPop(null)
      })
      .catch((e) => alive && setQueryError(`Index « ${index} » : ${e.message}`))
    return () => { alive = false }
  }, [index])

  /* ---- Bornes des dimensions « automatiques » ---- */
  const needed = schema ? statsNeeded(viz).filter((f) => !stats[f]) : []
  const neededKey = needed.join(',')
  useEffect(() => {
    if (!neededKey) return undefined
    const ac = new AbortController()
    const fmap = fieldMap(fields)
    const aggs = {}
    neededKey.split(',').forEach((f, i) => { aggs[`s${i}`] = { stats: { field: fmap.get(f)?.aggField || f } } })
    runQuery(index, { size: 0, aggs }, schema?.accessFilters, ac.signal)
      .then((r) => {
        const next = {}
        neededKey.split(',').forEach((f, i) => { next[f] = r.aggregations?.[`s${i}`] || { min: null, max: null } })
        setStats((s) => ({ ...s, ...next }))
      })
      .catch(() => {
        const next = {}
        neededKey.split(',').forEach((f) => { next[f] = { min: null, max: null } })
        setStats((s) => ({ ...s, ...next }))
      })
    return () => ac.abort()
  }, [neededKey, index, fields, schema])

  /* ---- Requête principale (en direct, anti-rebond) ---- */
  const dsl = useMemo(() => (schema && !neededKey ? buildQuery(viz, ctx) : null), [schema, viz, ctx, neededKey])
  const dslKey = JSON.stringify(dsl)
  useEffect(() => {
    if (!dsl) return undefined
    const ac = new AbortController()
    const t = setTimeout(() => {
      setLoading(true)
      runQuery(index, dsl, schema?.accessFilters, ac.signal)
        .then((r) => {
          setResponse(r)
          setResponseId((n) => n + 1)
          setQueryError('')
        })
        .catch((e) => { if (e.name !== 'AbortError') setQueryError(e.message) })
        .finally(() => { if (!ac.signal.aborted) setLoading(false) })
    }, 250)
    return () => { clearTimeout(t); ac.abort() }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dslKey, index])

  /* ---- État courant, mémorisé pour la prochaine visite ---- */
  const ui = useMemo(() => ({ index, timeField, range, query, viz, queryUuid, viewId, published }), [index, timeField, range, query, viz, queryUuid, viewId, published])
  useEffect(() => {
    if (!schema) return undefined
    const t = setTimeout(() => {
      request(`/api/orbit/saved-view/${VIEW_KEY}`, { method: 'POST', body: JSON.stringify({ auto: true, ui }) }).catch(() => {})
    }, 1500)
    return () => clearTimeout(t)
  }, [ui, schema])

  /* ---- Dérivés ---- */
  const block = useMemo(() => buildBlock(viz, fields), [viz, fields])
  const title = vizTitle(viz, fields)
  const missing = missingSlots(viz)
  const slots = SLOTS[viz.type]
  const sugg = useMemo(() => (schema && response ? suggestions(viz, ctx) : []), [viz, ctx, schema, response])
  const total = response?.hits?.total?.value
  const fmapAll = fieldMap(fields)
  const shownFields = fields.filter((f) => {
    const q = fieldSearch.trim().toLowerCase()
    return !q || f.name.toLowerCase().includes(q) || (f.label || '').toLowerCase().includes(q)
  })

  const editing = edit && slots.find((s) => s.key === edit.key)
  const editDim = editing && getSlot(viz, edit.key).find((d) => d.id === edit.id)
  useEffect(() => { if (edit && !editDim) setEdit(null) }, [edit, editDim])

  /* ---- Gestes ---- */
  const addToViz = (name) => commit(placeOnWorkspace(viz, fields, name, timeField))
  const onDragStart = (e, name) => {
    e.dataTransfer.setData('text/plain', name)
    e.dataTransfer.effectAllowed = 'copy'
    setDrag(name)
  }
  const endDrag = () => { setDrag(null); setOver(null) }
  const draggedField = (e) => {
    const name = e.dataTransfer.getData('text/plain')
    return isAggregatable(fmapAll.get(name)) || (viz.type === 'list' && fmapAll.has(name)) ? name : null
  }
  const dropWorkspace = (e) => {
    e.preventDefault()
    const name = draggedField(e)
    endDrag()
    if (name) addToViz(name)
  }
  const dropSlot = (e, slot) => {
    e.preventDefault()
    e.stopPropagation()
    const name = draggedField(e)
    endDrag()
    if (name) commit(placeOnSlot(viz, fields, slot, name))
  }
  const updateDim = (key, d) => setViz((v) => setSlot(v, key, getSlot(v, key).map((x) => (x.id === d.id ? d : x))))
  const removeDim = (key, id) => {
    commit(setSlot(viz, key, getSlot(viz, key).filter((x) => x.id !== id)))
    setEdit(null)
  }
  const addDim = (slot) => {
    const d = slot.kind === 'column'
      ? mk('field', fields[0]?.name ?? null)
      : slot.kind === 'metric'
      ? mk('count', null)
      : (timeField ? mk('date_histogram', timeField, { interval: 'auto' }) : mk('terms', fields.find(isAggregatable)?.name ?? null, { size: 5, orderBy: 'count' }))
    commit(setSlot(viz, slot.key, slot.multi ? [...getSlot(viz, slot.key), d] : [d]))
    setEdit({ key: slot.key, id: d.id })
  }
  const undo = () => {
    setViz(hist[hist.length - 1])
    setHist((h) => h.slice(0, -1))
  }
  const submitQuery = (e) => {
    e.preventDefault()
    setQuery(qDraft)
  }

  /* ---- Vues enregistrées ---- */
  const currentView = views.find((v) => String(v.id) === String(viewId))
  const openView = (id) => {
    setViewId(id)
    const v = views.find((x) => String(x.id) === String(id))
    const s = v?.state?.ui
    if (!s) return
    // Vue partagée posée sur un index que ce compte ne lit pas.
    if (!indexes.some((i) => i.id === s.index)) {
      setViewMsg(`Cette visualisation porte sur un index inaccessible (${s.index}).`)
      return
    }
    if (s.index !== index) {
      restored.current = { ...s, viewId: id }
      setIndex(s.index)
      return
    }
    // Un état encore EN ATTENTE des champs de son index (une vue ouverte
    // juste avant, sur un autre index) ne doit pas s'appliquer APRÈS celle-ci :
    // c'est ce qui mêlait deux visualisations (la requête de l'une, les
    // métriques de l'autre).
    restored.current = null
    setPublished(s.published || null)
    setTimeField(s.timeField ?? timeField)
    setRange(s.range || '30d')
    setQDraft(s.query || '')
    setQuery(s.query || '')
    commit(s.viz || viz)
    setQueryUuid(s.queryUuid || '')
  }
  const saveView = async (asNew) => {
    const name = asNew ? (saveName || '').trim() : currentView?.title
    if (!name) return
    try {
      const env = await request(`/api/orbit/saved-view/${VIEW_KEY}`, {
        method: 'POST',
        body: JSON.stringify({ ...(asNew ? {} : { id: currentView.id }), title: name, ui: { ...ui, viewId: '' } }),
      })
      setViews(env.views || [])
      if (env.savedId) setViewId(String(env.savedId))
      setSaveName(null)
      setViewMsg(`Visualisation « ${name} » enregistrée.`)
    }
    catch (e) { setViewMsg(e.message) }
  }
  const deleteView = async () => {
    if (!currentView || !window.confirm(`Supprimer la visualisation « ${currentView.title} » ?`)) return
    try {
      const env = await request(`/api/orbit/saved-view/${VIEW_KEY}/delete`, { method: 'POST', body: JSON.stringify({ id: currentView.id }) })
      setViews(env.views || [])
      setViewId('')
      setViewMsg('Visualisation supprimée.')
    }
    catch (e) { setViewMsg(e.message) }
  }

  /* ---- Assistant IA (Claude, via le sidecar) ---- */
  const askAi = async (text) => {
    const history = chat
      .filter((t) => t.role === 'user' || t.role === 'assistant')
      .map((t) => ({ role: t.role, text: t.text }))
    setChat((c) => [...c, { role: 'user', text }])
    setAiBusy(true)
    try {
      const r = await request('/api/orbit/lens/ai', {
        method: 'POST',
        body: JSON.stringify({ index, state: toAiState({ viz, timeField, range, query }), request: text, history }),
      })
      if (r.index && r.index !== index) {
        const label = indexes.find((i) => i.id === r.index)?.label || r.index
        setChat((c) => [...c, { role: 'assistant', text: r.message }, { role: 'note', text: `Index changé : ${label}.` }])
        restored.current = { index: r.index, aiState: r.state }
        setIndex(r.index)
        return
      }
      const s = fromAiState(r.state, fields)
      commit(s.viz)
      setTimeField(s.timeField)
      setRange(s.range)
      setQDraft(s.query)
      setQuery(s.query)
      setChat((c) => [
        ...c,
        { role: 'assistant', text: r.message },
        ...(s.warnings.length ? [{ role: 'note', text: s.warnings.join(' ') }] : []),
      ])
    }
    catch (e) {
      setChat((c) => [...c, { role: 'error', text: e.message }])
    }
    finally {
      setAiBusy(false)
    }
  }

  /* ---- Publication (bloc « Visualisation » des pages) ---- */
  const publish = async (asNew) => {
    const name = (asNew ? publishName : published?.title || publishName || title).trim()
    if (!name || !dsl) return
    try {
      const r = await request('/api/orbit/lens/visualizations', {
        method: 'POST',
        body: JSON.stringify({
          ...(asNew || !published ? {} : { id: published.id }),
          title: name,
          index,
          state: { index, timeField, range, query, viz },
          block: buildBlock(viz, fields),
          dsl,
        }),
      })
      setPublished({ id: r.id, title: r.title })
      setPublishName(null)
      setPublishMsg(`Publiée : « ${r.title} » (n° ${r.id}). Insérez-la dans une page avec le bloc « Visualisation ».`)
    }
    catch (e) { setPublishMsg(e.message) }
  }

  /* ---- Export : requête stockée + bloc YAML ---- */
  const storeQuery = async () => {
    if (!dsl) return
    const uuid = queryUuid || crypto.randomUUID()
    try {
      await request('/api/elasticsearch/query/save', {
        method: 'POST',
        body: JSON.stringify({ uuid, title: `Lens — ${title}`, body: dsl, accessFilters: schema?.accessFilters || [], renderMode: 'raw' }),
      })
      setQueryUuid(uuid)
      setStoreMsg(queryUuid ? 'Requête mise à jour.' : 'Requête enregistrée : le bloc ci-dessous est prêt à coller.')
    }
    catch (e) { setStoreMsg(e.message) }
  }
  const exportYaml = useMemo(
    () => yaml.dump([exportBlock(viz, fields, index, queryUuid)], { lineWidth: 120, noRefs: true }),
    [viz, fields, index, queryUuid],
  )

  /* ------------------------------------------------------------------ */

  if (catalogError) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-10">
        <div className="alert alert-danger" role="alert">{catalogError}</div>
      </div>
    )
  }
  if (!indexes) {
    return <div className="px-4 py-10 flex justify-center" role="status"><span className="spinner" /><span className="sr-only">Chargement…</span></div>
  }
  if (!indexes.length) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-10">
        <div className="empty-state"><p>Aucun index Elasticsearch n'est accessible à ce compte.</p></div>
      </div>
    )
  }

  const indexInfo = indexes.find((i) => i.id === index)

  return (
    <div className="px-4 md:px-6 py-4 flex flex-col gap-3" aria-labelledby="lens-title">
      {/* En-tête : titre + vues enregistrées */}
      <header className="flex flex-wrap items-center gap-3 justify-between">
        <div>
          <h1 id="lens-title" className="h-page">Lens</h1>
          <p className="text-sm text-slate-500">Graphiques et tableaux calculés en direct sur les index Elasticsearch d'Orbit.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label htmlFor="lens-view" className="sr-only">Visualisation enregistrée</label>
          <select id="lens-view" className="select w-64" value={viewId} onChange={(e) => (e.target.value ? openView(e.target.value) : setViewId(''))}>
            <option value="">Visualisations enregistrées…</option>
            {views.map((v) => <option key={v.id} value={v.id}>{v.title}{v.isMine ? '' : ` — ${v.ownerName}`}</option>)}
          </select>
          {currentView?.canEdit ? (
            <button type="button" className="btn btn-secondary" onClick={() => saveView(false)}><Save size={16} aria-hidden="true" />Enregistrer</button>
          ) : null}
          {saveName === null ? (
            <button type="button" className="btn btn-secondary" onClick={() => setSaveName(title)}>Enregistrer sous…</button>
          ) : (
            <form className="flex items-center gap-2" onSubmit={(e) => { e.preventDefault(); saveView(true) }}>
              <label htmlFor="lens-save-name" className="sr-only">Nom de la visualisation</label>
              <input id="lens-save-name" className="input" value={saveName} onChange={(e) => setSaveName(e.target.value)} autoFocus />
              <button type="submit" className="btn btn-primary">Enregistrer</button>
              <button type="button" className="btn btn-ghost" onClick={() => setSaveName(null)}>Annuler</button>
            </form>
          )}
          {currentView?.canDelete ? (
            <button type="button" className="btn btn-ghost-danger" onClick={deleteView} aria-label={`Supprimer la visualisation ${currentView.title}`}>
              <Trash2 size={16} aria-hidden="true" />
            </button>
          ) : null}
          <span role="status" className="text-xs text-slate-500">{viewMsg}</span>
        </div>
      </header>

      {/* Publication : ce que le bloc « Visualisation » insère dans une page. */}
      <div className="flex flex-wrap items-center gap-2 -mt-1">
        <span className="text-sm text-slate-600">
          {published ? <>Visualisation publiée : <strong>{published.title}</strong> (n° {published.id})</> : 'Non publiée'}
        </span>
        {published ? (
          <button type="button" className="btn btn-primary btn-sm" disabled={!dsl} onClick={() => publish(false)}>
            <Send size={14} aria-hidden="true" />Mettre à jour la publication
          </button>
        ) : null}
        {publishName === null ? (
          <button type="button" className="btn btn-secondary btn-sm" disabled={!dsl} onClick={() => setPublishName(title)}>
            {published ? 'Publier une copie…' : 'Publier…'}
          </button>
        ) : (
          <form className="flex items-center gap-2" onSubmit={(e) => { e.preventDefault(); publish(true) }}>
            <label htmlFor="lens-publish-name" className="sr-only">Titre de la visualisation publiée</label>
            <input id="lens-publish-name" className="input input-sm w-80" value={publishName} onChange={(e) => setPublishName(e.target.value)} autoFocus />
            <button type="submit" className="btn btn-primary btn-sm">Publier</button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setPublishName(null)}>Annuler</button>
          </form>
        )}
        <span role="status" className="text-xs text-slate-500">{publishMsg}</span>
      </div>

      {/* Barre de contexte : index, temps, requête */}
      <div className="card card-body flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="field-label">Index</span>
          <select className="select" value={index} onChange={(e) => setIndex(e.target.value)}>
            {indexes.map((i) => (
              <option key={i.id} value={i.id}>{i.label}{i.count != null ? ` (${NF.format(i.count)})` : ''}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="field-label">Champ de temps</span>
          <select className="select" value={timeField || ''} onChange={(e) => setTimeField(e.target.value || null)} disabled={!dateFields.length}>
            <option value="">Aucun</option>
            {dateFields.map((f) => <option key={f.name} value={f.name}>{f.label || f.name}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="field-label">Période</span>
          <select className="select" value={range} onChange={(e) => setRange(e.target.value)} disabled={!timeField}>
            {Object.entries(RANGES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </label>
        <form className="flex-1 min-w-64 flex items-end gap-2" role="search" onSubmit={submitQuery}>
          <label className="block flex-1">
            <span className="field-label">Filtre (syntaxe Lucene)</span>
            <input
              className={queryError && query ? 'input input-error w-full font-mono' : 'input w-full font-mono'}
              type="text"
              value={qDraft}
              onChange={(e) => setQDraft(e.target.value)}
              placeholder="ex. status:true AND amount:>1000"
            />
          </label>
          <button type="submit" className="btn btn-primary"><Search size={16} aria-hidden="true" />Appliquer</button>
        </form>
        <div className="flex items-center gap-2">
          <button type="button" className="btn btn-toggle" aria-pressed={aiOpen} onClick={() => setAiOpen(!aiOpen)}><Sparkles size={16} aria-hidden="true" />Assistant IA</button>
          <button type="button" className="btn btn-secondary" disabled={!hist.length} onClick={undo}><Undo2 size={16} aria-hidden="true" />Annuler</button>
          <button type="button" className="btn btn-toggle" aria-pressed={panel === 'inspect'} onClick={() => setPanel(panel === 'inspect' ? null : 'inspect')}><Eye size={16} aria-hidden="true" />Inspecter</button>
          <button type="button" className="btn btn-toggle" aria-pressed={panel === 'export'} onClick={() => setPanel(panel === 'export' ? null : 'export')}><Code2 size={16} aria-hidden="true" />Exporter</button>
        </div>
      </div>
      {queryError ? <div className="alert alert-danger" role="alert">{queryError}</div> : null}
      {aiOpen ? <AssistantPanel chat={chat} busy={aiBusy} onAsk={askAi} onClose={() => setAiOpen(false)} /> : null}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 items-start">
        {/* Champs */}
        <aside className="card lg:col-span-3 min-w-0" aria-labelledby="lens-fields">
          <div className="card-header flex items-center justify-between gap-2">
            <h2 id="lens-fields">Champs</h2>
            <span className="text-xs font-normal text-slate-500 truncate">{indexInfo?.label}</span>
          </div>
          <div className="p-3 flex flex-col gap-1">
            <label htmlFor="lens-field-search" className="sr-only">Rechercher un champ</label>
            <input id="lens-field-search" className="input input-sm w-full mb-2" type="search" placeholder="Rechercher un champ" value={fieldSearch} onChange={(e) => setFieldSearch(e.target.value)} />
            {!schema ? <p className="text-sm text-slate-500" role="status">Chargement des champs…</p> : null}
            <ul className="flex flex-col lg:max-h-screen overflow-y-auto">
              {shownFields.map((f) => {
                const agg = isAggregatable(f) || viz.type === 'list'
                const badge = KIND_BADGE[f.kind]
                return (
                  <li key={f.name}>
                    <div
                      className={`flex items-center gap-2 rounded-lg px-1.5 py-1 hover:bg-slate-50 ${agg ? 'cursor-grab' : ''}`}
                      draggable={agg}
                      onDragStart={(e) => onDragStart(e, f.name)}
                      onDragEnd={endDrag}
                    >
                      <span className={`badge badge-sm ${badge.cls} shrink-0`}>{badge.txt}</span>
                      <button
                        type="button"
                        className="flex-1 min-w-0 text-left text-sm truncate"
                        aria-expanded={pop === f.name}
                        onClick={() => setPop(pop === f.name ? null : f.name)}
                        title={f.label ? `${f.label} — ${f.name}` : f.name}
                      >
                        {f.label || f.name}
                        {f.label ? <span className="block text-xs text-slate-400 font-mono truncate">{f.name}</span> : null}
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm shrink-0"
                        aria-label={`Ajouter ${f.label || f.name} à la visualisation`}
                        title={agg ? 'Ajouter à la visualisation' : 'Champ plein texte : utilisable dans le filtre seulement'}
                        disabled={!agg}
                        onClick={() => addToViz(f.name)}
                      >
                        <Plus size={14} aria-hidden="true" />
                      </button>
                    </div>
                    {pop === f.name ? (
                      <div className="mx-1.5 my-1 p-2.5 rounded-lg border border-slate-200 bg-slate-50 flex flex-col gap-2">
                        {isAggregatable(f)
                          ? <FieldStats field={f} index={index} ctx={ctx} accessFilters={schema?.accessFilters} />
                          : <p className="text-xs text-slate-500">Champ plein texte : interrogeable dans le filtre (<code className="font-mono">{f.name}:mot</code>), pas agrégeable.</p>}
                        {agg ? (
                          <button type="button" className="btn btn-secondary btn-sm self-start" onClick={() => { addToViz(f.name); setPop(null) }}>Visualiser</button>
                        ) : null}
                      </div>
                    ) : null}
                  </li>
                )
              })}
            </ul>
            {schema && !shownFields.length ? <p className="text-sm text-slate-500">Aucun champ ne correspond.</p> : null}
            <p className="text-xs text-slate-500 pt-2">Glissez un champ sur le graphique ou sur un emplacement, ou utilisez + (clavier : Tab puis Entrée).</p>
          </div>
        </aside>

        {/* Espace de travail */}
        <section className="card lg:col-span-6 min-w-0" aria-labelledby="lens-viz-title">
          <div
            className={`card-body rounded-xl border-2 border-dashed ${over === 'ws' ? 'border-iris-400 bg-iris-50' : drag ? 'border-slate-300' : 'border-transparent'}`}
            onDragOver={(e) => { e.preventDefault(); setOver('ws') }}
            onDragLeave={() => setOver((o) => (o === 'ws' ? null : o))}
            onDrop={dropWorkspace}
          >
            <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
              <h2 id="lens-viz-title" className="h-section">{title}</h2>
              <span className="text-xs text-slate-500" role="status">
                {loading ? 'Calcul…' : total != null ? `${NF.format(total)} enregistrements · ${timeField && range !== 'all' ? RANGES[range].toLowerCase() : 'toute la période'}` : ''}
              </span>
            </div>
            {missing.length ? (
              <div className="empty-state">
                <p className="font-semibold text-slate-800">Configuration incomplète</p>
                <p>Ajoutez un champ à « {missing[0].label} », ou glissez un champ ici pour laisser l'éditeur choisir.</p>
              </div>
            ) : response ? (
              <BlockView block={block} response={viz.type === 'list' ? asContents(response) : response} responseId={responseId} plugins={plugins} />
            ) : (
              <div className="empty-state" role="status"><span className="spinner" /><span className="sr-only">Calcul…</span></div>
            )}
          </div>

          {sugg.length && !missing.length ? (
            <div className="card-footer">
              <h3 className="stat-label mb-2">Suggestions</h3>
              <div className="flex gap-2 overflow-x-auto pb-1">
                {sugg.map((s) => {
                  const Icon = TYPE_ICON[s.type]
                  return (
                    <button
                      key={s.type}
                      type="button"
                      className="card card-interactive shrink-0 px-3 py-2 text-left"
                      onClick={() => commit(s.viz)}
                      aria-label={`Appliquer la suggestion : ${TYPES[s.type]}`}
                    >
                      <span className="flex items-center gap-2 text-sm font-medium"><Icon size={18} aria-hidden="true" className="text-iris-600" />{TYPES[s.type]}</span>
                    </button>
                  )
                })}
              </div>
            </div>
          ) : null}
        </section>

        {/* Configuration */}
        <aside className="card lg:col-span-3 min-w-0" aria-labelledby="lens-config">
          <div className="card-header"><h2 id="lens-config">Configuration</h2></div>
          <div className="card-body">
            {editing && editDim ? (
              <DimensionEditor
                slot={editing}
                dim={editDim}
                fields={fields}
                onChange={(d) => updateDim(edit.key, d)}
                onRemove={() => removeDim(edit.key, editDim.id)}
                onClose={() => setEdit(null)}
              />
            ) : (
              <div className="flex flex-col gap-4">
                <fieldset>
                  <legend className="field-label">Type de visualisation</legend>
                  <div className="grid grid-cols-3 gap-1.5">
                    {Object.entries(TYPES).map(([k, l]) => {
                      const Icon = TYPE_ICON[k]
                      return (
                        <button
                          key={k}
                          type="button"
                          className="btn btn-toggle btn-sm flex-col h-auto py-2"
                          aria-pressed={viz.type === k}
                          title={l}
                          onClick={() => commit(switchType(viz, k, timeField))}
                        >
                          <Icon size={18} aria-hidden="true" />
                          <span className="text-xs">{TYPE_SHORT[k]}</span>
                        </button>
                      )
                    })}
                  </div>
                </fieldset>

                {viz.type === 'list' ? (
                  <div className="flex flex-col gap-2">
                    <label className="block">
                      <span className="field-label">Nombre de lignes (documents)</span>
                      <input
                        className="input w-full"
                        type="number"
                        min="1"
                        max="200"
                        value={viz.size || 50}
                        onChange={(e) => commit({ ...viz, size: Math.max(1, Math.min(200, Number(e.target.value) || 1)) })}
                      />
                    </label>
                    <label className="checkbox-row">
                      <input
                        type="checkbox"
                        className="checkbox"
                        checked={Boolean(viz.transpose)}
                        onChange={(e) => commit({ ...viz, transpose: e.target.checked })}
                      />
                      <span>Présenter en fiche (une ligne par champ : libellé / valeur)</span>
                    </label>
                  </div>
                ) : null}

                {viz.type === 'line' ? (
                  <label className="checkbox-row">
                    <input
                      type="checkbox"
                      className="checkbox"
                      checked={Boolean(viz.rebase)}
                      onChange={(e) => commit({ ...viz, rebase: e.target.checked })}
                    />
                    <span>Base 100 au début de la période (comparer des évolutions)</span>
                  </label>
                ) : null}

                {slots.map((s) => {
                  const dims = getSlot(viz, s.key)
                  return (
                    <div key={s.key} className="flex flex-col gap-1.5">
                      <div className="flex justify-between text-xs text-slate-500">
                        <span className="font-medium">{s.label}</span>
                        {s.req && !dims.length ? <span className="text-rose-700">Requis</span> : null}
                      </div>
                      <div
                        className={`rounded-lg border-2 border-dashed p-1 flex flex-col gap-1 ${over === s.key ? 'border-iris-400 bg-iris-50' : 'border-slate-200'}`}
                        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setOver(s.key) }}
                        onDragLeave={() => setOver((o) => (o === s.key ? null : o))}
                        onDrop={(e) => dropSlot(e, s)}
                      >
                        {dims.map((d) => (
                          <div key={d.id} className="flex items-center gap-1 rounded-md border border-slate-200 bg-white">
                            <button type="button" className="flex-1 min-w-0 text-left text-sm px-2 py-1.5 truncate" onClick={() => setEdit({ key: s.key, id: d.id })} title={dimLabel(d, fields)}>
                              {dimLabel(d, fields)}
                            </button>
                            <button type="button" className="btn btn-ghost-danger btn-sm" aria-label={`Retirer ${dimLabel(d, fields)}`} onClick={() => removeDim(s.key, d.id)}>
                              <X size={14} aria-hidden="true" />
                            </button>
                          </div>
                        ))}
                        {s.multi || !dims.length ? (
                          <button type="button" className="text-left text-sm text-slate-500 hover:text-iris-700 px-2 py-1.5" onClick={() => addDim(s)}>
                            + Déposer un champ ou cliquer pour ajouter
                          </button>
                        ) : null}
                      </div>
                      {s.key === 'y' && viz.breakdown && dims.length > 1 ? (
                        <p className="field-hint">Avec une ventilation, seule la première métrique est représentée.</p>
                      ) : null}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </aside>
      </div>

      {/* Inspecter : les données calculées et la requête */}
      {panel === 'inspect' && response && !missing.length ? (
        <section className="card" aria-labelledby="lens-inspect">
          <div className="card-header"><h2 id="lens-inspect">Données</h2></div>
          <div className="card-body flex flex-col gap-4">
            {viz.type === 'metric' || viz.type === 'list'
              ? <p className="text-sm text-slate-500">Pas de table de données agrégées pour ce type : voir la requête ci-dessous.</p>
              : <BlockView block={{ ...buildBlock({ ...viz, type: 'table' }, fields), totalRowLabel: 'none' }} response={response} responseId={responseId} plugins={plugins} />}
            <details>
              <summary className="cursor-pointer text-sm font-medium">Requête Elasticsearch</summary>
              <pre className="mt-2 p-3 rounded-lg bg-slate-50 border border-slate-200 text-xs font-mono overflow-x-auto">{JSON.stringify(dsl, null, 2)}</pre>
            </details>
          </div>
        </section>
      ) : null}

      {/* Exporter : bloc reactive-json à coller */}
      {panel === 'export' && dsl ? (
        <section className="card" aria-labelledby="lens-export">
          <div className="card-header"><h2 id="lens-export">Exporter</h2></div>
          <div className="card-body flex flex-col gap-3">
            <p className="text-sm text-slate-600">
              Le bloc reactive-json ci-dessous (<code className="font-mono">ChartLoader</code>) se colle dans une page YAML ou un bloc du builder.
              Il interroge une <strong>requête enregistrée</strong> (nœud <code className="font-mono">elasticsearch_query</code>), qui porte ses filtres d'accès
              {schema?.accessFilters?.length ? ` (${schema.accessFilters.join(', ')})` : ''}.
            </p>
            {canStoreQuery ? (
              <div className="flex flex-wrap items-center gap-2">
                <button type="button" className="btn btn-primary btn-sm" onClick={storeQuery}>
                  <Save size={14} aria-hidden="true" />{queryUuid ? 'Mettre à jour la requête enregistrée' : 'Enregistrer la requête'}
                </button>
                <span role="status" className="text-xs text-slate-500">{storeMsg}</span>
              </div>
            ) : (
              <p className="field-hint">Enregistrer la requête demande la permission « Administer Elasticsearch queries ».</p>
            )}
            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between gap-2">
                <h3 className="field-label mb-0">Bloc YAML</h3>
                <CopyButton text={exportYaml} label="Copier le YAML" />
              </div>
              <pre className="p-3 rounded-lg bg-slate-50 border border-slate-200 text-xs font-mono overflow-x-auto">{exportYaml}</pre>
            </div>
            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between gap-2">
                <h3 className="field-label mb-0">Requête Elasticsearch</h3>
                <CopyButton text={JSON.stringify(dsl, null, 2)} label="Copier la requête" />
              </div>
              <pre className="p-3 rounded-lg bg-slate-50 border border-slate-200 text-xs font-mono overflow-x-auto max-h-80">{JSON.stringify(dsl, null, 2)}</pre>
            </div>
          </div>
        </section>
      ) : null}
    </div>
  )
}
